import { logger } from 'packages/logger';
import api from './api';
import utils from '../checkout/utils';
import { setFetching, setCartData, setError } from './reducer';
import * as analytics from 'tradera/apps/tradera/components/vip/helpers/analytics';
import cookieUtil from 'tradera/utils/cookie';
import { CART_ID } from 'tradera/constants/cookies';
import { selectShippingCountry } from 'tradera/state/shipping-region/selectors';
import { tagTypes, util } from 'tradera/state/services/cart-api';

export const fetchCartData = (cartId) => async (dispatch, getState) => {
    dispatch(setFetching(true));

    try {
        const { countryCodeIso2 } = selectShippingCountry(getState());
        const checkoutData = await api.getCartOverview(cartId, countryCodeIso2);

        const { data } = checkoutData;

        dispatch(setCartData(data));
    } catch (error) {
        logger(error);
        dispatch(setFetching(false));
    }
};

export const addItemToCart =
    (cartId, itemId, price, onSuccess) => async (dispatch) => {
        dispatch(setFetching(true));

        try {
            const addItemResult = await api.addItemToCart(
                cartId,
                itemId,
                price
            );
            if (addItemResult.status === 200) {
                // eslint-disable-next-line better-mutation/no-mutation
                cartId = addItemResult.data.cartId;
                const date = new Date();
                date.setMonth(date.getMonth() + 2);
                cookieUtil.createCookie(CART_ID, cartId, date);
            }
            // KATS: This is only manually dispatched since not all of the cart is in RTK query yet
            dispatch(util.invalidateTags([tagTypes.CartSummary]));
            onSuccess();
        } catch (error) {
            logger(error);
            dispatch(setFetching(false));
        }
    };

export const removeItemsFromCart = (cartId, itemIds) => async (dispatch) => {
    dispatch(setFetching(true));

    try {
        await api.removeItemsFromCart(cartId, itemIds);
        dispatch(fetchCartData(cartId));
        // KATS: This is only manually dispatched since not all of the cart is in RTK query yet
        dispatch(util.invalidateTags([tagTypes.CartSummary]));
    } catch (error) {
        logger(error);
        dispatch(setFetching(false));
    }
};

export const setCartItemQuantity =
    (cartId, itemId, quantity) => async (dispatch) => {
        dispatch(setFetching(true));

        try {
            await api.setCartItemQuantity(cartId, itemId, quantity);
            dispatch(fetchCartData(cartId));
            // KATS: This is only manually dispatched since not all of the cart is in RTK query yet
            dispatch(util.invalidateTags([tagTypes.CartSummary]));
        } catch (error) {
            logger(error);
            dispatch(setFetching(false));
        }
    };

export const confirmCartPurchase =
    (cartId, preorderId, shopItems, onSuccess) =>
    async (dispatch, getState) => {
        dispatch(setFetching(true));

        try {
            const environment = getState().environment;

            const {
                isHybridAppContextForAndroid,
                isHybridAppContextForIos,
                variables,
                featureSwitches,
                appInstanceId,
                gaTrackingSessionId
            } = environment;

            const googleAnalyticsData = analytics.getGaTrackingData(
                featureSwitches['google-analytics-improved-tracking'],
                variables.PUBLIC_GOOGLE_ANALYTICS_TRACKING_ID,
                isHybridAppContextForIos,
                isHybridAppContextForAndroid,
                appInstanceId,
                gaTrackingSessionId
            );
            const confirmData = await api.confirmCartPurchase(
                shopItems,
                googleAnalyticsData
            );

            const { countryCodeIso2 } = selectShippingCountry(getState());
            await removeFromCartWithoutReload(
                cartId,
                shopItems.map((item) => item.itemId),
                countryCodeIso2
            );

            onSuccess(confirmData.data.transactionIds[0]);
        } catch (error) {
            logger(error);
            dispatch(setFetching(false));

            const errorCode = error?.response?.data?.responseStatus?.errorCode;
            dispatch(setError({ id: preorderId, message: errorCode }));
        }
    };

export const createUncommittedPurchaseOrder =
    (cartId, preorderId, sellerMemberId, shopItems, onSuccess) =>
    async (dispatch) => {
        dispatch(setFetching(true));
        const purchaseOrderId = utils.getUuid();

        try {
            await api.createUncommittedPurchaseOrder(
                purchaseOrderId,
                sellerMemberId,
                shopItems.flatMap((item) =>
                    Array(item.quantity).fill({
                        itemId: item.itemId,
                        itemPrice: item.expectedItemPrice
                    })
                )
            );

            onSuccess(purchaseOrderId);
        } catch (error) {
            logger(error);
            dispatch(setFetching(false));

            const errorCode = error?.response?.data?.responseStatus?.errorCode;
            dispatch(setError({ id: preorderId, message: errorCode }));
        }
    };

export const removeFromCartWithoutReload = async (
    cartId,
    itemIds,
    countryCodeIso2
) => {
    if (!cartId) return;
    await api.removeItemsFromCart(cartId, itemIds);

    const { data } = await api.getCartOverview(cartId, countryCodeIso2);
    setCartData(data);
};
